/**
 * Simulated GET /api/gps/dispatch — the "morning roll-out" board
 * (TruckDispatch, inside Company → Operations Summary).
 *
 * Shape is `GpsDispatch` from src/lib/api/gpsDispatch.ts, field for field.
 *
 * Owner's spec constants, honoured here:
 *   • shop geofence 40.908169, -74.410299 with a 250 m radius
 *   • monitored window 06:00–09:35 ET, scored window 08:00–09:30 ET
 *     (`window.start_min` / `window.end_min` are the SCORED window, which is
 *     what "minutes after 8" is measured against)
 *
 * Derived from the snapshot: the fleet is the real `trucks` table (label,
 * driver, GPS vehicle label) and every "currently at" address is a real loss
 * address from `v_jobs_full`.
 *
 * Generated: one departure time per truck per weekday, seeded from the truck
 * label and the date — so yesterday's board is the same on every reload, and
 * the weekly / monthly / by-weekday averages are the true averages of the days
 * shown.
 */
import { loadTable } from '~/demo/data';
import { once, seededFloat, seededInt } from './prng';
import { nyDate, nyMinutes, round2, str } from './finance';

export const SHOP_LAT = 40.908169;
export const SHOP_LON = -74.410299;
export const SHOP_RADIUS_M = 250;
/** Monitored window: 06:00 → 09:35 ET. */
export const MONITOR_START_MIN = 6 * 60;
export const MONITOR_END_MIN = 9 * 60 + 35;
/** Scored window: 08:00 → 09:30 ET. */
export const SCORED_START_MIN = 8 * 60;
export const SCORED_END_MIN = 9 * 60 + 30;

interface Truck { label: string; driver: string; gpsLabel: string }
interface Departure { out: boolean; departedMin: number }

const isWeekday = (d: Date) => d.getDay() !== 0 && d.getDay() !== 6;

function hhmm(dateStr: string, minutes: number): string {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${dateStr}T${h}:${m}:00`;
}

/** The one deterministic fact per truck-day everything else is built from. */
function departureFor(label: string, date: string): Departure {
  const out = seededFloat(`${label}|${date}|out`, 0, 1) > 0.12;
  // Most trucks roll out inside the monitored window; a few slip past it.
  const departedMin = seededInt(`${label}|${date}|dep`, MONITOR_START_MIN + 80, MONITOR_END_MIN + 10);
  return { out, departedMin };
}

const fleet = once(async (): Promise<Truck[]> => {
  const rows = await loadTable('trucks');
  return rows
    .filter((t) => t.is_active !== false)
    .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0))
    .map((t) => ({
      label: str(t.label),
      driver: str(t.driver_name),
      gpsLabel: str(t.gps_vehicle_label) || str(t.label),
    }))
    .filter((t) => t.label);
});

/** Real loss addresses, so a truck is never "at" an invented street. */
const addresses = once(async (): Promise<string[]> => {
  const jobs = await loadTable('v_jobs_full');
  const out: string[] = [];
  for (const j of jobs) {
    const line = [str(j.loss_address1), str(j.loss_city), str(j.loss_state)].filter(Boolean).join(', ');
    if (line.includes(',')) out.push(line);
    if (out.length >= 400) break;
  }
  return out;
});

function pastWeekdays(from: Date, count: number): string[] {
  const out: string[] = [];
  const d = new Date(from);
  while (out.length < count) {
    if (isWeekday(d)) out.push(nyDate(d));
    d.setDate(d.getDate() - 1);
  }
  return out.reverse();
}

function averages(labels: string[], dates: string[]) {
  const byTruck = labels.map((label) => {
    const days = dates.map((date) => ({ date, ...departureFor(label, date) }));
    const outDays = days.filter((d) => d.out);
    const avgDepartedMin = outDays.length
      ? round2(outDays.reduce((s, d) => s + d.departedMin, 0) / outDays.length) : null;
    return {
      label,
      avgDepartedMin,
      avgMinutesAfter8: avgDepartedMin == null ? null : round2(avgDepartedMin - SCORED_START_MIN),
      daysOut: outDays.length,
      daysNotOut: days.length - outDays.length,
    };
  });
  const departures = byTruck.reduce((s, t) => s + t.daysOut, 0);
  const withData = byTruck.filter((t) => t.avgDepartedMin != null);
  const avgDepartedMin = withData.length
    ? round2(withData.reduce((s, t) => s + (t.avgDepartedMin ?? 0), 0) / withData.length) : null;
  return {
    dates,
    byTruck,
    company: {
      avgDepartedMin,
      avgMinutesAfter8: avgDepartedMin == null ? null : round2(avgDepartedMin - SCORED_START_MIN),
      departures,
    },
  };
}

export async function simulatedGpsDispatch(dateParam?: string) {
  const [trucks, addrs] = await Promise.all([fleet(), addresses()]);
  const now = new Date();
  const todayStr = nyDate(now);
  const date = dateParam || todayStr;
  const isHistorical = date !== todayStr;
  const day = new Date(`${date}T12:00:00`);
  const weekday = isWeekday(day);
  const nowMin = isHistorical ? null : nyMinutes(now);
  const pastWindow = isHistorical || (nowMin ?? 0) > MONITOR_END_MIN;

  const labels = trucks.map((t) => t.label);

  const todayTrucks = trucks.map((truck, i) => {
    const dep = weekday ? departureFor(truck.label, date) : { out: false, departedMin: 0 };
    const departed = dep.out && (isHistorical || (nowMin ?? 0) >= dep.departedMin);
    const inShopNow = !departed;
    const status: 'out' | 'late' | 'not_out' | 'at_shop' | 'unknown' =
      !weekday ? 'unknown'
        : departed ? (dep.departedMin > SCORED_END_MIN ? 'late' : 'out')
          : pastWindow ? 'not_out' : 'at_shop';

    const addr = addrs.length ? addrs[seededInt(`${truck.label}|${date}|addr`, 0, addrs.length - 1)] : '';
    const ageMinutes = seededInt(`${truck.label}|${date}|age`, 1, 26);
    const jitterLat = (seededFloat(`${truck.label}|${date}|lat`, -1, 1)) * (departed ? 0.09 : 0.0015);
    const jitterLon = (seededFloat(`${truck.label}|${date}|lon`, -1, 1)) * (departed ? 0.11 : 0.0015);
    const currentAddress = departed ? addr : 'PDQ Restoration — shop yard';

    return {
      label: truck.label,
      status,
      departedAt: departed ? hhmm(date, dep.departedMin) : null,
      departedMin: departed ? dep.departedMin : null,
      minutesAfter8: departed ? dep.departedMin - SCORED_START_MIN : null,
      seenAtShop: weekday,
      inShopNow: isHistorical ? null : inShopNow,
      currentAddress,
      ageMinutes,
      gps: {
        id: `demo-gps-${i + 1}`,
        rawLabel: truck.gpsLabel,
        lat: Math.round((SHOP_LAT + jitterLat) * 1e6) / 1e6,
        lon: Math.round((SHOP_LON + jitterLon) * 1e6) / 1e6,
        lastUpdate: new Date(now.getTime() - ageMinutes * 60_000).toISOString(),
        ageMinutes,
        address: currentAddress,
        live: !isHistorical,
      },
      driver: truck.driver,
    };
  });

  const weekly = averages(labels, pastWeekdays(day, 5));
  const monthly = averages(labels, pastWeekdays(day, 22));

  const byWeekday = [1, 2, 3, 4, 5].map((dow) => {
    const dates = monthly.dates.filter((d) => new Date(`${d}T12:00:00`).getDay() === dow);
    const values: number[] = [];
    for (const label of labels) {
      for (const d of dates) {
        const dep = departureFor(label, d);
        if (dep.out) values.push(dep.departedMin);
      }
    }
    const avg = values.length ? round2(values.reduce((s, v) => s + v, 0) / values.length) : null;
    return {
      dow,
      avgDepartedMin: avg,
      avgMinutesAfter8: avg == null ? null : round2(avg - SCORED_START_MIN),
      samples: values.length,
    };
  });

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Fleet and driver names are the real trucks table and every "currently at" address is a real v_jobs_full loss '
      + 'address; each truck-day departure is generated deterministically inside the 06:00–09:35 ET monitored window '
      + 'and scored against the 08:00–09:30 ET window, 250 m shop geofence at 40.908169, -74.410299.',
    fetched_at: now.toISOString(),
    shop: { lat: SHOP_LAT, lon: SHOP_LON, radius_m: SHOP_RADIUS_M },
    monitored_window: { start_min: MONITOR_START_MIN, end_min: MONITOR_END_MIN },
    window: { start_min: SCORED_START_MIN, end_min: SCORED_END_MIN },
    today: {
      date,
      dow: day.getDay(),
      is_weekday: weekday,
      now_min: nowMin,
      past_window: pastWindow,
      is_historical: isHistorical,
      trucks: todayTrucks,
    },
    weekly,
    monthly,
    byWeekday,
    live_available: true,
  };
}
